// src/store/slices/filesSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { fileEndpoints } from '../../services/api/endpoints/file.endpoint';
import { FileItem } from '../../types/file';

interface FilesState {
  files: FileItem[];
  isLoading: boolean;
  ingestPolling: boolean; // global long-poll flag
  error: string | null;
}

const initialState: FilesState = {
  files: [],
  isLoading: false,
  ingestPolling: false,
  error: null,
};

// Async thunk: fetch file list
export const fetchFiles = createAsyncThunk(
  'files/fetchFiles',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      return await fileEndpoints.listFiles(organizationId);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch files');
    }
  }
);

const filesSlice = createSlice({
  name: 'files',
  initialState,
  reducers: {
    // standalone action to toggle the long-poll flag
    setIngestPolling: (state, action) => {
      state.ingestPolling = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchFiles.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFiles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.files = action.payload;
      })
      .addCase(fetchFiles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setIngestPolling } = filesSlice.actions;

// selectors
export const selectFiles = (state: RootState) => state.files.files;
export const selectIsLoading = (state: RootState) => state.files.isLoading;
export const selectIngestPolling = (state: RootState) => state.files.ingestPolling;

export default filesSlice.reducer;
